import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  GuardResult,
  MaybeAsync,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { filter, map, of, switchMap, take } from 'rxjs';
import { RoutePath } from '../enums/routes.enum';
import { FirebaseAuthService } from '../services/firebase-auth.service';
import { UserService } from '../services/user.service';

export const registerGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): MaybeAsync<GuardResult> => {
  const userService = inject(UserService);
  const authService = inject(FirebaseAuthService);
  const router = inject(Router);

  const registeredAppRedirectUrl = () => {
    const redirectUrl = authService.redirectUrl();
    if (redirectUrl) {
      authService.setRedirectUrl(null);
      return router.parseUrl(redirectUrl);
    }
    return router.createUrlTree([RoutePath.Dashboard]);
  };

  return authService.state
    .select('token')
    .pipe(
      filter((token) => token != null),
      take(1),
    )
    .pipe(
      switchMap(() =>
        userService.isUserRegistered$.pipe(
          switchMap((isRegisteredStatic) => {
            // check cached value so an API call is not needed
            if (isRegisteredStatic) {
              return of(
                state.url.startsWith(`/${RoutePath.RegisterUser}`)
                  ? registeredAppRedirectUrl()
                  : true,
              );
            }
            return userService.checkIfUserRegistered$().pipe(
              map((isRegistered) => {
                // Pages accessible when not registered
                if (state.url.startsWith(`/${RoutePath.RegisterUser}`)) {
                  return isRegistered ? registeredAppRedirectUrl() : true;
                }

                // Others
                if (isRegistered) {
                  return true;
                }
                authService.setRedirectUrl(state.url);
                return router.createUrlTree([RoutePath.RegisterUser]);
              }),
            );
          }),
        ),
      ),
    );
};
